import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Plus, Trash2, ChevronUp, ChevronDown, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const uuid = () => crypto.randomUUID();

const I = (p) => <input {...p} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-prepify-500/40" />;

const NavigationEditor = () => {
  const [nav, setNav] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/cms/navigation`).then((r) => setNav(r.data)).catch(() => toast.error("Failed to load"));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/cms/navigation`, nav);
      toast.success("Navigation saved");
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  if (!nav) return <div className="text-slate-400">Loading…</div>;

  // Header items
  const addHeaderItem = () => setNav({ ...nav, header_items: [...nav.header_items, { id: uuid(), label: "New", href: "/", is_external: false, open_in_new_tab: false, order: nav.header_items.length, is_visible: true }] });
  const updateHeaderItem = (i, patch) => { const next = [...nav.header_items]; next[i] = { ...next[i], ...patch }; setNav({ ...nav, header_items: next }); };
  const moveHeader = (i, dir) => { const next = [...nav.header_items]; const j = i + dir; if (j < 0 || j >= next.length) return; [next[i], next[j]] = [next[j], next[i]]; setNav({ ...nav, header_items: next.map((it, idx) => ({ ...it, order: idx })) }); };
  const removeHeader = (i) => setNav({ ...nav, header_items: nav.header_items.filter((_, j) => j !== i) });

  // Footer columns
  const addColumn = () => setNav({ ...nav, footer_columns: [...nav.footer_columns, { id: uuid(), heading: "New Column", items: [], order: nav.footer_columns.length }] });
  const updateColumn = (ci, patch) => { const next = [...nav.footer_columns]; next[ci] = { ...next[ci], ...patch }; setNav({ ...nav, footer_columns: next }); };
  const moveColumn = (ci, dir) => { const next = [...nav.footer_columns]; const j = ci + dir; if (j < 0 || j >= next.length) return; [next[ci], next[j]] = [next[j], next[ci]]; setNav({ ...nav, footer_columns: next.map((c, idx) => ({ ...c, order: idx })) }); };
  const removeColumn = (ci) => setNav({ ...nav, footer_columns: nav.footer_columns.filter((_, j) => j !== ci) });
  const addColItem = (ci) => { const col = nav.footer_columns[ci]; const newItem = { id: uuid(), label: "New", href: "/", is_external: false, open_in_new_tab: false, order: col.items.length, is_visible: true }; updateColumn(ci, { items: [...col.items, newItem] }); };
  const updateColItem = (ci, ii, patch) => { const items = [...nav.footer_columns[ci].items]; items[ii] = { ...items[ii], ...patch }; updateColumn(ci, { items }); };
  const removeColItem = (ci, ii) => updateColumn(ci, { items: nav.footer_columns[ci].items.filter((_, j) => j !== ii) });
  const moveColItem = (ci, ii, dir) => { const items = [...nav.footer_columns[ci].items]; const j = ii + dir; if (j < 0 || j >= items.length) return; [items[ii], items[j]] = [items[j], items[ii]]; updateColumn(ci, { items: items.map((it, idx) => ({ ...it, order: idx })) }); };

  const ItemRow = ({ item, onChange, onUp, onDown, onRemove, canUp, canDown }) => (
    <div className="grid grid-cols-12 gap-2 items-center py-1">
      <div className="col-span-4"><I placeholder="Label" value={item.label} onChange={(e) => onChange({ label: e.target.value })} /></div>
      <div className="col-span-5"><I placeholder="/slug or #anchor or https://…" value={item.href} onChange={(e) => onChange({ href: e.target.value })} /></div>
      <div className="col-span-3 flex items-center gap-1 justify-end">
        <label className="text-xs flex items-center gap-1" title="Open in new tab"><input type="checkbox" checked={item.open_in_new_tab} onChange={(e) => onChange({ open_in_new_tab: e.target.checked })} /> ↗</label>
        <button onClick={() => onChange({ is_visible: !item.is_visible })} className={`p-1 ${item.is_visible ? "text-prepify-600" : "text-slate-400"}`}>{item.is_visible ? <Eye size={14} /> : <EyeOff size={14} />}</button>
        <button onClick={onUp} disabled={!canUp} className="p-1 disabled:opacity-30 hover:bg-slate-100 rounded"><ChevronUp size={14} /></button>
        <button onClick={onDown} disabled={!canDown} className="p-1 disabled:opacity-30 hover:bg-slate-100 rounded"><ChevronDown size={14} /></button>
        <button onClick={onRemove} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
      </div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Navigation</h1>
          <p className="text-sm text-slate-500">Configure the header menu and footer columns used across the entire site.</p>
        </div>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-slate-900 hover:bg-prepify-600 text-white font-medium px-5 py-2.5 rounded-lg disabled:opacity-50" data-testid="save-nav-btn">
          <Save size={16} /> {saving ? "Saving…" : "Save"}
        </button>
      </header>

      <section className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Header Menu</h3>
          <button onClick={addHeaderItem} className="text-xs bg-prepify-600 hover:bg-prepify-700 text-white px-3 py-1.5 rounded inline-flex items-center gap-1"><Plus size={12} /> Add link</button>
        </div>
        <div className="space-y-1">
          {nav.header_items.map((it, i) => (
            <ItemRow key={it.id} item={it} onChange={(p) => updateHeaderItem(i, p)} onUp={() => moveHeader(i, -1)} onDown={() => moveHeader(i, 1)} onRemove={() => removeHeader(i)} canUp={i > 0} canDown={i < nav.header_items.length - 1} />
          ))}
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Footer Columns</h3>
          <button onClick={addColumn} className="text-xs bg-prepify-600 hover:bg-prepify-700 text-white px-3 py-1.5 rounded inline-flex items-center gap-1"><Plus size={12} /> Add column</button>
        </div>
        <div className="space-y-4">
          {nav.footer_columns.map((col, ci) => (
            <div key={col.id} className="border border-slate-200 rounded-xl p-4 bg-slate-50/40">
              <div className="flex gap-2 mb-3">
                <I placeholder="Column heading" value={col.heading} onChange={(e) => updateColumn(ci, { heading: e.target.value })} />
                <button onClick={() => moveColumn(ci, -1)} disabled={ci === 0} className="p-1.5 hover:bg-white rounded disabled:opacity-30"><ChevronUp size={14} /></button>
                <button onClick={() => moveColumn(ci, 1)} disabled={ci === nav.footer_columns.length - 1} className="p-1.5 hover:bg-white rounded disabled:opacity-30"><ChevronDown size={14} /></button>
                <button onClick={() => removeColumn(ci)} className="p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
              </div>
              <div className="space-y-1">
                {col.items.map((it, ii) => (
                  <ItemRow key={it.id} item={it} onChange={(p) => updateColItem(ci, ii, p)} onUp={() => moveColItem(ci, ii, -1)} onDown={() => moveColItem(ci, ii, 1)} onRemove={() => removeColItem(ci, ii)} canUp={ii > 0} canDown={ii < col.items.length - 1} />
                ))}
              </div>
              <button onClick={() => addColItem(ci)} className="text-xs text-prepify-700 hover:underline mt-2">+ Add link</button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default NavigationEditor;
